import {
    getTestRun,
    listTestRuns,
    listTestRunsByDefinition,
} from './queries';

import {
    GraphQLQuery,
    GraphQLResult,
} from '@aws-amplify/api';

import {
    GetTestRun,
    ListTestRuns,
    ListTestRunsByDefinition,
    TestRun,
} from '../types.ts';
import {getTestingApiClient} from '../../testing-api-integration/client.ts';

// READ-ONLY (FNP-969). There are no add/update/delete functions in this service
// and there must not be: the three TestRun mutations are @aws_iam-only, so a
// console call would fail authorization at the API even if someone wrote one.

// `listTestRuns` accepts TestRunsFilterInput — testDefinitionId, syntheticUserId
// and status ONLY. Anything else is rejected by AppSync as an unknown field, so
// the filter is whitelisted here rather than passed through wholesale.
const FILTERABLE_FIELDS = [
    'testDefinitionId',
    'syntheticUserId',
    'status',
] as const;

export const toFilterInput = (filter: Record<string, string> = {}) => {
    const input = FILTERABLE_FIELDS.reduce<Record<string, string>>((acc, field) => {
        const value = filter[field];
        // An empty string is not "no filter" as far as the store is concerned —
        // it is a real value that matches nothing. Drop it.
        if (typeof value === 'string' && value.trim() !== '') {
            acc[field] = value.trim();
        }
        return acc;
    }, {});

    // An empty object would be sent as `filter: {}`, which AppSync treats as a
    // filter with no predicates rather than as an absent filter. Send undefined.
    return Object.keys(input).length > 0 ? input : undefined;
};

// Drains nextToken. The API pages at its own limit, and a caller asking for "the
// runs" means all of them — a single page would silently under-report, which for
// a module whose job is counting ABANDONED vs FAILED runs is a wrong answer
// rather than a partial one.
export const fetchRecords = async (filter: Record<string, string> = {}) => {
    const filterInput = toFilterInput(filter);

    const items: TestRun[] = [];
    let nextToken: string | undefined = undefined;

    do {
        // Annotated explicitly: `nextToken` is assigned FROM this response and
        // also read by the query that produces it, so inference goes circular
        // (TS7022) without a written-out type here.
        const response: GraphQLResult<ListTestRuns> =
            await getTestingApiClient().graphql<GraphQLQuery<ListTestRuns>>({
                query: listTestRuns,
                variables: {
                    filter: filterInput,
                    nextToken: nextToken,
                },
            });
        items.push(...(response.data?.listTestRuns.items ?? []));
        nextToken = response.data?.listTestRuns.nextToken;
    } while (nextToken);

    return items;
};

export const fetch = async (id: string) => {
    const response = await getTestingApiClient().graphql<GraphQLQuery<GetTestRun>>({
        query: getTestRun,
        variables: {id: id},
    });
    return response.data?.getTestRun;
};

// The by-definition index. Kept because it is a genuinely different access
// pattern (a GSI query rather than a filtered list) and is what the definition
// detail view needs to answer "how has this test been doing".
export const fetchRecordsByDefinition = async (testDefinitionId: string) => {
    const items: TestRun[] = [];
    let nextToken: string | undefined = undefined;

    do {
        // Annotated explicitly for the same TS7022 reason as fetchRecords above.
        const response: GraphQLResult<ListTestRunsByDefinition> =
            await getTestingApiClient().graphql<GraphQLQuery<ListTestRunsByDefinition>>({
                query: listTestRunsByDefinition,
                variables: {
                    testDefinitionId: testDefinitionId,
                    nextToken: nextToken,
                },
            });
        items.push(...(response.data?.listTestRunsByDefinition.items ?? []));
        nextToken = response.data?.listTestRunsByDefinition.nextToken;
    } while (nextToken);

    return items;
};
